import { useEffect, useMemo, useState } from 'react';

import http from '@/lib/http';
import { cmsSectionTemplatesPath } from '@/lib/routes';
import { isBrowser } from '@/lib/utils';
import type {
  SectionTemplateItem,
  SectionTemplatesListingResponse,
} from '@/types/section-template';

interface UseSectionTemplatesResult {
  templates: SectionTemplateItem[];
  templatesByCode: Map<string, SectionTemplateItem>;
  isLoading: boolean;
  error: string | null;
}

let cachedTemplates: SectionTemplateItem[] | null = null;
let pendingTemplatesRequest: Promise<SectionTemplateItem[]> | null = null;

const canUseClientCache = (): boolean => isBrowser();

const fetchSectionTemplates = async (): Promise<SectionTemplateItem[]> => {
  const response = await http.get<SectionTemplatesListingResponse>(cmsSectionTemplatesPath());
  return response.data.sectionTemplates;
};

const loadSectionTemplates = async (): Promise<SectionTemplateItem[]> => {
  if (!canUseClientCache()) {
    return fetchSectionTemplates();
  }

  if (cachedTemplates) {
    return cachedTemplates;
  }

  if (pendingTemplatesRequest) {
    return pendingTemplatesRequest;
  }

  pendingTemplatesRequest = fetchSectionTemplates()
    .then((sectionTemplates) => {
      cachedTemplates = sectionTemplates;
      return sectionTemplates;
    })
    .finally(() => {
      pendingTemplatesRequest = null;
    });

  return pendingTemplatesRequest;
};

export function useSectionTemplates(): UseSectionTemplatesResult {
  const [templates, setTemplates] = useState<SectionTemplateItem[]>(() =>
    canUseClientCache() && cachedTemplates ? cachedTemplates : [],
  );
  const [isLoading, setIsLoading] = useState(() =>
    canUseClientCache() ? cachedTemplates == null : true,
  );
  const [error, setError] = useState<string | null>(null);
  const templatesByCode = useMemo(
    () => new Map(templates.map((template) => [template.code, template])),
    [templates],
  );

  useEffect(() => {
    let cancelled = false;

    const fetchTemplates = async () => {
      if (!cancelled) {
        setError(null);
        setIsLoading(true);
      }

      try {
        const sectionTemplates = await loadSectionTemplates();

        if (!cancelled) {
          setTemplates(sectionTemplates);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load section templates');
          console.error('Failed to fetch section templates', err);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void fetchTemplates();

    return () => {
      cancelled = true;
    };
  }, []);

  return { templates, templatesByCode, isLoading, error };
}
